import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, ArrowUpRight, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import TableToolbar, { useVisibleColumns } from "@/components/TableToolbar";

const ALL_COLUMNS = [
  { key: "id", label: "Enquiry #", alwaysOn: true },
  { key: "garage", label: "Garage" },
  { key: "vehicle", label: "Vehicle" },
  { key: "items", label: "Items" },
  { key: "created", label: "Created" },
  { key: "status", label: "Status", alwaysOn: true },
];

const VIEWS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "submitted", label: "Submitted" },
  { key: "quoted", label: "Quoted" },
  { key: "closed", label: "Closed" },
];

const StatusPill = ({ status }) => {
  const map = {
    new: { bg: "#F7900915", fg: "#F79009", label: "New" },
    submitted: { bg: "#457b9d20", fg: "#457b9d", label: "Submitted" },
    quoted: { bg: "#12B76A15", fg: "#12B76A", label: "Quoted" },
    closed: { bg: "#66708515", fg: "#667085", label: "Closed" },
  }[status] || { bg: "#66708515", fg: "#667085", label: status || "—" };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: map.bg, color: map.fg }}>
      {map.label}
    </span>
  );
};

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [visibleCols, toggleCol] = useVisibleColumns("admin_enquiries", ALL_COLUMNS);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (view !== "all") params.status_f = view;
    api.get("/admin/enquiries", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [view]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const isVisible = (k) => ALL_COLUMNS.find((c) => c.key === k)?.alwaysOn || visibleCols.has(k);

  return (
    <div className="space-y-5" data-testid="admin-enquiries-root">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Operations</div>
        <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Parts Enquiries</h1>
        <p className="text-[13px] text-[#667085] mt-0.5">Every spare-parts enquiry submitted to SparesHub, across all garages.</p>
      </div>

      <TableToolbar
        testIdPrefix="admin-enquiries"
        searchValue={q}
        onSearchChange={setQ}
        searchPlaceholder="Search by ID or garage…"
        views={VIEWS}
        activeView={view}
        onViewChange={setView}
        columns={ALL_COLUMNS}
        visibleColumns={visibleCols}
        onToggleColumn={toggleCol}
      />

      {/* Desktop */}
      <div className="hidden md:block bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-enquiries-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Enquiry #</th>
              {isVisible("garage") && <th className="text-left px-4 py-3 font-semibold">Garage</th>}
              {isVisible("vehicle") && <th className="text-left px-4 py-3 font-semibold">Vehicle</th>}
              {isVisible("items") && <th className="text-right px-4 py-3 font-semibold">Items</th>}
              {isVisible("created") && <th className="text-left px-4 py-3 font-semibold">Created</th>}
              <th className="text-left px-4 py-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-[13px] text-[#667085]">
                  No enquiries in this view yet.
                </td>
              </tr>
            )}
            {items.map((e) => (
              <tr key={e.id} className="hover:bg-[#F7F9FC]" data-testid={`admin-enquiry-row-${e.id}`}>
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/enquiries/${e.id}`}
                    data-testid={`admin-enquiry-link-${e.id}`}
                    className="inline-flex items-center gap-2 text-[#1d3557] hover:text-[#e63946] font-semibold"
                  >
                    <Send size={13} className="text-[#457b9d]" />
                    {e.enquiry_number || e.id?.slice(0, 8)}
                  </Link>
                </td>
                {isVisible("garage") && (
                  <td className="px-4 py-3">
                    {e.garage ? (
                      <Link
                        to={`/admin/garages/${e.garage.id}`}
                        className="inline-flex items-center gap-1 text-[12px] text-[#457b9d] hover:text-[#e63946] font-semibold"
                      >
                        {e.garage.name} <ArrowUpRight size={11} />
                      </Link>
                    ) : (
                      <span className="text-[12px] text-[#a8dadc]">—</span>
                    )}
                  </td>
                )}
                {isVisible("vehicle") && (
                  <td className="px-4 py-3 text-[12px] text-[#1d3557]">
                    {e.vehicle_reg || "—"}
                    {e.vehicle_make ? <div className="text-[11px] text-[#667085]">{e.vehicle_make} {e.vehicle_model}</div> : null}
                  </td>
                )}
                {isVisible("items") && (
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center gap-1 text-[12px] font-mono text-[#1d3557] font-semibold">
                      <Package size={12} className="text-[#457b9d]" /> {(e.items || []).length}
                    </span>
                  </td>
                )}
                {isVisible("created") && (
                  <td className="px-4 py-3 text-[11px] font-mono text-[#667085]">
                    {e.created_at ? fmtDateTime(e.created_at) : "—"}
                  </td>
                )}
                <td className="px-4 py-3"><StatusPill status={e.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="md:hidden space-y-2.5" data-testid="admin-enquiries-cards">
        {items.map((e) => (
          <Link
            key={e.id}
            to={`/admin/enquiries/${e.id}`}
            data-testid={`admin-enquiry-card-${e.id}`}
            className="block bg-white rounded-lg border border-[#E4E7EC] p-3.5 active:bg-[#F7F9FC]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-[#1d3557] truncate flex items-center gap-1">
                  {e.enquiry_number || e.id?.slice(0, 8)} <ArrowUpRight size={12} className="text-[#a8dadc]" />
                </div>
                <div className="text-[11px] text-[#667085] mt-0.5">
                  {e.garage?.name || "—"} · {(e.items || []).length} item(s) · {e.vehicle_reg || ""}
                </div>
              </div>
              <StatusPill status={e.status} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
